/**
 * Training Admin Panel — Settings → Training (spec §7 + B-4).
 *
 * Tabs: Overview · Users · Topics (script editor) · Settings.
 *
 * Every write hits /api/training/admin/* which is audit-logged
 * server-side. No optimistic UI for destructive actions — we always
 * re-fetch after mutate to guarantee the UI reflects persisted state.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getAdminSettings, patchAdminSettings,
  getAdminTopics, getAdminUserProgress, getAdminAnalytics,
  createAdminTopic, updateAdminTopic, deleteAdminTopic,
} from "@/lib/trainingAdminApi";
import useConfirm from "@/hooks/useConfirm";

const CANONICAL_ROLES = [
  "super_admin", "crm_admin", "sales_manager", "sales_agent",
  "call_agent", "demo_agent", "bookings_agent", "contracts_agent",
  "marketing_manager", "marketing_agent", "read_only",
];

const ROLE_LABEL = {
  super_admin: "Super Admin", crm_admin: "CRM Admin", sales_manager: "Sales Manager",
  sales_agent: "Sales Agent", call_agent: "Call Agent", demo_agent: "Demo Agent",
  bookings_agent: "Bookings Agent", contracts_agent: "Contracts Agent",
  marketing_manager: "Marketing Manager", marketing_agent: "Marketing Agent",
  read_only: "Read Only",
};

function Tile({ label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 140 }}
    >
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: "#111" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        border: "none", background: "transparent",
        padding: "8px 14px", fontSize: 13,
        color: active ? "#2563eb" : "#666", fontWeight: active ? 600 : 500,
        borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
        cursor: "pointer", marginRight: 4,
      }}
    >
      {children}
    </button>
  );
}

// ── Overview tab ────────────────────────────────────────────────────────
function OverviewTab({ analytics, settings }) {
  if (!analytics) return <div style={{ padding: 20, color: "#888" }}>Loading…</div>;
  const s = analytics.summary || {};
  return (
    <div style={{ padding: "0 4px" }} data-testid="training-admin-overview">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <Tile label="Total topics" value={s.total_topics ?? 0} testid="tile-total-topics" />
        <Tile label="Total completions" value={s.total_completions ?? 0} testid="tile-total-completions" />
        <Tile label="Active learners" value={`${s.active_learners ?? 0} / ${s.total_users ?? 0}`} sub="users with any activity" testid="tile-active-learners" />
        <Tile label="Mandatory onboarding" value={s.mandatory_onboarding_enabled ? "ON" : "OFF"} sub={`${s.onboarding_complete_rate_pct ?? 0}% completed`} testid="tile-mandatory" />
      </div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Top topics by completion</div>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="analytics-topics-table">
        <thead>
          <tr style={{ textAlign: "left", color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Topic</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Category</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Steps</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Completions</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>In progress</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Skips</th>
          </tr>
        </thead>
        <tbody>
          {(analytics.topics || []).slice(0, 20).map((t) => (
            <tr key={t.slug} data-testid={`analytics-topic-row-${t.slug}`}>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>
                {t.title}
                {!t.is_published && <span style={{ marginLeft: 6, fontSize: 9, color: "#9A1C1C", background: "#FDE2E2", padding: "1px 5px", borderRadius: 10 }}>DRAFT</span>}
              </td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", color: "#888" }}>{t.category}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{t.step_count}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{t.completion_count}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{t.in_progress_count}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{t.skip_step_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────
function UsersTab({ userRows }) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (userRows || []).filter((u) =>
      (roleFilter === "all" || u.role === roleFilter) &&
      (!q ||
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q))
    );
  }, [userRows, query, roleFilter]);

  return (
    <div data-testid="training-admin-users">
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search users…" data-testid="admin-users-search"
          style={{ flex: 1, border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 12px", fontSize: 12, outline: "none" }}
        />
        <select
          value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="admin-users-role-filter"
          style={{ border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px", fontSize: 12, background: "#fff" }}
        >
          <option value="all">All roles</option>
          {CANONICAL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
      </div>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="admin-users-table">
        <thead>
          <tr style={{ textAlign: "left", color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Name</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Role</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Completed</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Total minutes</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Last trained</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((u) => (
            <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>
                <div style={{ fontWeight: 500 }}>{u.full_name || "(no name)"}</div>
                <div style={{ color: "#888", fontSize: 11 }}>{u.email}</div>
              </td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", color: "#888" }}>{ROLE_LABEL[u.role] || u.role}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{u.completed_count}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{u.total_minutes || 0}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", color: "#888" }}>{u.last_trained_at ? new Date(u.last_trained_at).toLocaleString() : "—"}</td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={5} style={{ padding: "20px 10px", color: "#888", textAlign: "center" }}>No users match your filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Topics tab + script editor ──────────────────────────────────────────
function TopicsTab({ topics, onReload }) {
  const [editing, setEditing] = useState(null); // topic object or null for create
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (topics || []).filter((t) =>
      !q ||
      (t.title || "").toLowerCase().includes(q) ||
      (t.slug || "").toLowerCase().includes(q) ||
      (t.category || "").toLowerCase().includes(q)
    );
  }, [topics, query]);

  return (
    <div data-testid="training-admin-topics">
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search topics…" data-testid="admin-topics-search"
          style={{ flex: 1, border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 12px", fontSize: 12, outline: "none" }}
        />
        <button
          type="button" onClick={() => { setCreating(true); setEditing({}); }}
          data-testid="admin-new-topic"
          style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
        >
          + New topic
        </button>
      </div>

      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="admin-topics-table">
        <thead>
          <tr style={{ textAlign: "left", color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Title</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Category</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Steps</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Roles</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}>Status</th>
            <th style={{ padding: "8px 10px", borderBottom: "0.5px solid #eee" }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((t) => (
            <tr key={t.slug} data-testid={`admin-topic-row-${t.slug}`}>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>
                <span aria-hidden style={{ marginRight: 6 }}>{t.icon || "🎓"}</span>
                <span style={{ fontWeight: 500 }}>{t.title}</span>
                <div style={{ color: "#888", fontSize: 10 }}>{t.slug}</div>
              </td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", color: "#888" }}>{t.category}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>{(t.steps || []).length}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", color: "#888", fontSize: 10 }}>{(t.roles || []).length} role{(t.roles || []).length !== 1 ? "s" : ""}</td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4" }}>
                {t.is_published ? (
                  <span style={{ fontSize: 9, color: "#3B6D11", background: "#EAF3DE", padding: "1px 6px", borderRadius: 10, fontWeight: 600 }}>PUBLISHED</span>
                ) : (
                  <span style={{ fontSize: 9, color: "#9A1C1C", background: "#FDE2E2", padding: "1px 6px", borderRadius: 10, fontWeight: 600 }}>DRAFT</span>
                )}
              </td>
              <td style={{ padding: "8px 10px", borderBottom: "0.5px solid #f4f4f4", textAlign: "right" }}>
                <button
                  type="button"
                  onClick={() => { setCreating(false); setEditing(t); }}
                  data-testid={`admin-edit-topic-${t.slug}`}
                  style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ padding: "20px 10px", color: "#888", textAlign: "center" }}>No topics.</td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <ScriptEditorModal
          topic={creating ? null : editing}
          isCreate={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); onReload(); }}
          onDeleted={() => { setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}

function ScriptEditorModal({ topic, isCreate, onClose, onSaved, onDeleted }) {
  const confirm = useConfirm();
  const [form, setForm] = useState(() => ({
    slug: topic?.slug || "",
    title: topic?.title || "",
    category: topic?.category || "General",
    icon: topic?.icon || "🎓",
    description: topic?.description || "",
    estimated_minutes: topic?.estimated_minutes || 2,
    role_badge: topic?.role_badge || "",
    roles: topic?.roles || [...CANONICAL_ROLES],
    is_published: topic?.is_published !== false,
    stepsJson: JSON.stringify(topic?.steps || [], null, 2),
  }));
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  const handleSave = useCallback(async () => {
    let parsed;
    try { parsed = JSON.parse(form.stepsJson); if (!Array.isArray(parsed)) throw new Error("steps must be an array"); }
    catch (e) { setJsonError(e.message); return; }
    setJsonError(null);
    setSaving(true);
    const payload = {
      title: form.title, category: form.category, icon: form.icon,
      description: form.description, estimated_minutes: Number(form.estimated_minutes) || 0,
      role_badge: form.role_badge || null,
      roles: form.roles, steps: parsed, is_published: form.is_published,
    };
    try {
      if (isCreate) {
        if (!form.slug.trim()) { toast.error("Slug is required"); setSaving(false); return; }
        await createAdminTopic({ slug: form.slug.trim(), ...payload });
        toast.success(`Created topic "${form.title}"`);
      } else {
        await updateAdminTopic(topic.slug, payload);
        toast.success(`Updated topic "${form.title}"`);
      }
      onSaved();
    } catch (err) {
      const s = err?.response?.status; const d = err?.response?.data?.detail;
      toast.error(s === 409 ? "Slug already exists" : (typeof d === "string" ? d : "Save failed"));
    } finally {
      setSaving(false);
    }
  }, [form, isCreate, topic, onSaved]);

  const handleDelete = useCallback(async () => {
    if (!topic?.slug) return;
    const ok = await confirm({
      title: "Delete topic?",
      body: `Delete topic "${topic.title}"? This cannot be undone.`,
      confirmLabel: "Delete topic",
      confirmTone: "danger",
      testid: "training-topic-delete-confirm",
    });
    if (!ok) return;
    try {
      await deleteAdminTopic(topic.slug);
      toast.success(`Deleted "${topic.title}"`);
      onDeleted();
    } catch {
      toast.error("Delete failed");
    }
  }, [topic, onDeleted, confirm]);

  const toggleRole = (role) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
    }));
  };

  return (
    <div
      data-testid="script-editor-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: 760, maxWidth: "94vw", background: "#fff", borderRadius: 12, boxShadow: "0 14px 48px rgba(0,0,0,0.2)", padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{isCreate ? "Create topic" : `Edit: ${topic?.title}`}</div>
            <div style={{ fontSize: 11, color: "#888" }}>Script changes are audit-logged and visible to all users instantly.</div>
          </div>
          <button onClick={onClose} data-testid="script-editor-close" style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#888" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          {isCreate && (
            <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
              Slug *
              <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                     data-testid="script-editor-slug"
                     style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
            </label>
          )}
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666", gridColumn: isCreate ? "auto" : "span 2" }}>
            Title
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                   data-testid="script-editor-title"
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
            Category
            <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
            Icon
            <input value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
            Description
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
            Est. minutes
            <input type="number" min="0" value={form.estimated_minutes} onChange={(e) => setForm((f) => ({ ...f, estimated_minutes: e.target.value }))}
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>
            Role badge
            <input value={form.role_badge} onChange={(e) => setForm((f) => ({ ...f, role_badge: e.target.value }))} placeholder="e.g. Admin+"
                   style={{ width: "100%", marginTop: 4, border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 400 }} />
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 6 }}>Visible to roles</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {CANONICAL_ROLES.map((r) => {
              const on = form.roles.includes(r);
              return (
                <button
                  key={r} type="button" onClick={() => toggleRole(r)}
                  data-testid={`script-editor-role-${r}`}
                  style={{
                    border: on ? "1px solid #2563eb" : "0.5px solid #ddd",
                    background: on ? "#f0f7ff" : "#fff",
                    color: on ? "#185FA5" : "#666",
                    borderRadius: 14, padding: "3px 10px", fontSize: 10, fontWeight: 500, cursor: "pointer",
                  }}
                >
                  {ROLE_LABEL[r]}
                </button>
              );
            })}
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#111", marginBottom: 12 }}>
          <input type="checkbox" checked={form.is_published}
                 onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
                 data-testid="script-editor-published" />
          Published (visible in the topic picker)
        </label>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
            <span>Steps (JSON array)</span>
            <span style={{ color: "#888", fontWeight: 400 }}>
              {(() => {
                try { const p = JSON.parse(form.stepsJson); return Array.isArray(p) ? `${p.length} step${p.length !== 1 ? "s" : ""}` : "invalid"; }
                catch { return "invalid JSON"; }
              })()}
            </span>
          </div>
          <textarea
            value={form.stepsJson}
            onChange={(e) => { setForm((f) => ({ ...f, stepsJson: e.target.value })); setJsonError(null); }}
            data-testid="script-editor-steps-json"
            rows={12}
            style={{ width: "100%", fontFamily: "monospace", fontSize: 11, border: jsonError ? "1px solid #9A1C1C" : "0.5px solid #ddd", borderRadius: 6, padding: 10, outline: "none", resize: "vertical", lineHeight: 1.5 }}
          />
          {jsonError && <div style={{ color: "#9A1C1C", fontSize: 11, marginTop: 4 }} data-testid="script-editor-json-error">JSON error: {jsonError}</div>}
          <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
            Each step: {"{"}instruction, spotlight (CSS selector, null allowed), route (pathname, null allowed), auto_advance ("click" | "next"){"}"}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          {!isCreate ? (
            <button type="button" onClick={handleDelete} data-testid="script-editor-delete"
                    style={{ border: "0.5px solid #F3C6C6", background: "#FFF7F7", color: "#9A1C1C", borderRadius: 6, padding: "7px 12px", fontSize: 11, cursor: "pointer" }}>
              Delete topic
            </button>
          ) : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose}
                    style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 6, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} data-testid="script-editor-save"
                    style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : (isCreate ? "Create topic" : "Save changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Settings tab ─────────────────────────────────────────────────────
function SettingsTab({ settings, topics, onReload }) {
  const [local, setLocal] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(settings || {}); }, [settings]);

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      const res = await patchAdminSettings(patch);
      setLocal(res);
      toast.success("Settings saved");
      onReload();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  }, [onReload]);

  const toggleRole = (role) => {
    const next = { ...(local.role_toggles || {}) };
    next[role] = !(next[role] ?? true);
    save({ role_toggles: next });
  };

  const toggleMandatory = () => {
    save({ mandatory_onboarding: !local.mandatory_onboarding });
  };

  const setRequiredForRole = (role, topicIds) => {
    const next = { ...(local.required_topics_by_role || {}) };
    next[role] = topicIds;
    save({ required_topics_by_role: next });
  };

  return (
    <div data-testid="training-admin-settings" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <section>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Training Mode availability</div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>Turn Training Mode on or off for each role. Disabled roles won't see the sidebar toggle.</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CANONICAL_ROLES.map((r) => {
            const on = (local.role_toggles || {})[r] !== false;
            return (
              <button
                key={r} type="button" onClick={() => toggleRole(r)}
                data-testid={`admin-role-toggle-${r}`}
                aria-pressed={on}
                disabled={saving}
                style={{
                  border: on ? "1px solid #2563eb" : "0.5px solid #ddd",
                  background: on ? "#f0f7ff" : "#fff",
                  color: on ? "#185FA5" : "#666",
                  borderRadius: 16, padding: "5px 12px", fontSize: 11, fontWeight: 500, cursor: saving ? "wait" : "pointer",
                }}
              >
                {ROLE_LABEL[r]} · {on ? "ON" : "OFF"}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Mandatory onboarding</div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#111" }}>
          <input type="checkbox" checked={!!local.mandatory_onboarding} onChange={toggleMandatory}
                 disabled={saving} data-testid="admin-mandatory-toggle" />
          Require new users to complete their required topics before using the CRM
        </label>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          When ON, non-completed users will see a blocking onboarding overlay until they finish their required topics (per-role list below).
        </div>
      </section>

      <section>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Required topics per role</div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
          Pick the topics each role MUST complete. These are marked as "Required" in the topic picker and counted in the onboarding completion rate.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {CANONICAL_ROLES.map((r) => {
            const selected = new Set((local.required_topics_by_role || {})[r] || []);
            return (
              <div key={r} data-testid={`admin-required-row-${r}`} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{ROLE_LABEL[r]}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {(topics || []).filter((t) => (t.roles || []).includes(r)).map((t) => {
                    const on = selected.has(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`admin-required-${r}-${t.slug}`}
                        onClick={() => {
                          const next = new Set(selected);
                          if (on) next.delete(t.id); else next.add(t.id);
                          setRequiredForRole(r, Array.from(next));
                        }}
                        disabled={saving}
                        style={{
                          border: on ? "1px solid #9A1C1C" : "0.5px solid #ddd",
                          background: on ? "#FDE2E2" : "#fff",
                          color: on ? "#9A1C1C" : "#666",
                          borderRadius: 12, padding: "3px 9px", fontSize: 10, fontWeight: 500, cursor: saving ? "wait" : "pointer",
                        }}
                      >
                        {on ? "✓ " : ""}{t.title}
                      </button>
                    );
                  })}
                  {(topics || []).filter((t) => (t.roles || []).includes(r)).length === 0 && (
                    <span style={{ fontSize: 10, color: "#888" }}>(no role-visible topics)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ── Root panel ─────────────────────────────────────────────────────────
export default function TrainingAdminPanel() {
  const [tab, setTab] = useState("overview");
  const [settings, setSettings] = useState(null);
  const [topics, setTopics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [userRows, setUserRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [s, t, a, u] = await Promise.all([
        getAdminSettings(), getAdminTopics(), getAdminAnalytics(), getAdminUserProgress(),
      ]);
      setSettings(s);
      setTopics(t.topics || []);
      setAnalytics(a);
      setUserRows(u.users || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setErr("Admin access required.");
      else setErr("Failed to load training admin data.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !settings) {
    return <div data-testid="training-admin-loading" style={{ padding: 24, color: "#888", fontSize: 12 }}>Loading training admin…</div>;
  }
  if (err) {
    return <div data-testid="training-admin-error" style={{ padding: 24, color: "#9A1C1C", fontSize: 12 }}>{err}</div>;
  }

  return (
    <div className="card" style={{ padding: "18px 22px" }} data-testid="training-admin-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0, flex: 1 }}>Training</h2>
      </div>

      <div style={{ borderBottom: "0.5px solid #eee", marginBottom: 16 }}>
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview">Overview</TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">Users</TabButton>
        <TabButton active={tab === "topics"} onClick={() => setTab("topics")} testid="tab-topics">Topics</TabButton>
        <TabButton active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">Settings</TabButton>
      </div>

      {tab === "overview" && <OverviewTab analytics={analytics} settings={settings} />}
      {tab === "users" && <UsersTab userRows={userRows} />}
      {tab === "topics" && <TopicsTab topics={topics} onReload={reload} />}
      {tab === "settings" && <SettingsTab settings={settings} topics={topics} onReload={reload} />}
    </div>
  );
}
